import React, { useState, useEffect, useCallback } from 'react';
import api from '../hooks/useApi';
import DateInput from '../components/DateInput';
import Icon from '../components/Icon';
import './TaxesTVQ.css';

// ─── HELPERS ────────────────────────────────
const fmt$ = (n) =>
  new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + ' $';

const fmtPct = (n) =>
  new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n || 0) + '%';

const TRIMESTRES = [
  { num: 1, label: 'T1',  periode: 'Jan — Mar' },
  { num: 2, label: 'T2',  periode: 'Avr — Juin' },
  { num: 3, label: 'T3',  periode: 'Juil — Sep' },
  { num: 4, label: 'T4',  periode: 'Oct — Déc' },
];

function badgeStatut(statut) {
  if (statut === 'payee')    return <span className="badge badge--success">Payée</span>;
  if (statut === 'produite') return <span className="badge badge--warning">Produite</span>;
  return <span className="badge">En cours</span>;
}

// ─── COMPOSANT PRINCIPAL ─────────────────────
export default function TaxesTVQ() {
  const anneeActuelle = new Date().getFullYear();
  const [annee, setAnnee]           = useState(anneeActuelle);
  const [config, setConfig]         = useState(null);
  const [alerte, setAlerte]         = useState(null);
  const [declarations, setDecl]     = useState([]);
  const [resume, setResume]         = useState(null);
  const [configOuvert, setConfigOuvert] = useState(false);
  const [formConfig, setFormConfig] = useState({});
  const [sauvegarde, setSauvegarde] = useState(false);
  const [calcul, setCalcul]         = useState({});  // { trimestre: loading }
  const [erreur, setErreur]         = useState(null);

  // ─── CHARGEMENT ─────────────────────────────
  const chargerConfig = useCallback(async () => {
    try {
      const d = await api.get('/taxes/config');
      setConfig(d);
      setFormConfig({
        numero_tps: d.numero_tps || '',
        numero_tvq: d.numero_tvq || '',
        date_inscription_tps: d.date_inscription_tps || '',
        date_inscription_tvq: d.date_inscription_tvq || '',
        frequence_declaration: d.frequence_declaration || 'trimestrielle',
        inscrit: d.inscrit === 1,
        notes: d.notes || '',
      });
    } catch { /* config absente */ }
  }, []);

  const chargerAlerte = useCallback(async () => {
    try {
      const d = await api.get('/taxes/alerte-seuil');
      setAlerte(d);
    } catch { /* silencieux */ }
  }, []);

  const chargerDeclarations = useCallback(async () => {
    try {
      const rows = await api.get(`/taxes/declarations?annee=${annee}`);
      setDecl(Array.isArray(rows) ? rows : []);
    } catch { setDecl([]); }
  }, [annee]);

  const chargerResume = useCallback(async () => {
    try {
      const d = await api.get(`/taxes/resume/${annee}`);
      setResume(d);
    } catch { setResume(null); }
  }, [annee]);

  useEffect(() => {
    chargerConfig();
    chargerAlerte();
  }, [chargerConfig, chargerAlerte]);

  useEffect(() => {
    chargerDeclarations();
    chargerResume();
  }, [chargerDeclarations, chargerResume]);

  // ─── SAUVEGARDER CONFIG ──────────────────────
  const sauvegarderConfig = async () => {
    setSauvegarde(true);
    setErreur(null);
    try {
      const d = await api.put('/taxes/config', {
        numero_tps: formConfig.numero_tps || null,
        numero_tvq: formConfig.numero_tvq || null,
        date_inscription_tps: formConfig.date_inscription_tps || null,
        date_inscription_tvq: formConfig.date_inscription_tvq || null,
        frequence_declaration: formConfig.frequence_declaration,
        inscrit: formConfig.inscrit ? 1 : 0,
        notes: formConfig.notes || null,
      });
      setConfig(d);
      chargerAlerte();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSauvegarde(false);
    }
  };

  // ─── CALCULER UN TRIMESTRE ───────────────────
  const calculerTrimestre = async (trimestre) => {
    setCalcul(prev => ({ ...prev, [trimestre]: true }));
    setErreur(null);
    try {
      await api.post(`/taxes/declarations/calculer/${annee}/${trimestre}`);
      await chargerDeclarations();
      await chargerResume();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors du calcul');
    } finally {
      setCalcul(prev => ({ ...prev, [trimestre]: false }));
    }
  };

  // ─── CHANGER STATUT ─────────────────────────
  const changerStatut = async (id, statut) => {
    try {
      await api.put(`/taxes/declarations/${id}/statut`, { statut });
      await chargerDeclarations();
      await chargerResume();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur');
    }
  };

  // ─── TROUVER DÉCLARATION PAR TRIMESTRE ───────
  const getDecl = (t) => declarations.find(d => d.trimestre === t) || null;

  // ─── COULEUR JAUGE ───────────────────────────
  function couleurJauge(pct) {
    if (pct >= 90) return 'var(--danger)';
    if (pct >= 75) return 'var(--warning)';
    return 'var(--success)';
  }

  // ─── RENDU ───────────────────────────────────
  return (
    <div className="tvq-page">

      {/* ── TITRE ── */}
      <div className="tvq-header">
        <div className="tvq-header-text">
          <h1 className="tvq-titre">Taxes TPS / TVQ</h1>
          <p className="tvq-sous-titre muted">Surveillance du seuil d'inscription et déclarations trimestrielles</p>
        </div>
      </div>

      {erreur && (
        <div className="error-inline" style={{ marginBottom: 'var(--space-4)' }}>
          <Icon name="alerte" size={16} />{erreur}
        </div>
      )}

      {/* ── NOTE FISCALE ── */}
      <div className="card tvq-note-fiscale">
        <div className="tvq-note-inner">
          <span className="tvq-note-icon info"><Icon name="info" size={18} /></span>
          <div>
            <strong className="info">Note fiscale importante</strong>
            <p className="muted" style={{ marginTop: 'var(--space-1)', fontSize: 13, lineHeight: 1.6 }}>
              Les règles de taxabilité des aliments au Canada sont complexes. Les aliments de base sont
              généralement détaxés, mais les aliments préparés peuvent être taxables. La classification
              des produits Escamel (congelés, préparés) doit être <strong>validée par votre comptable</strong> avant l'inscription.
            </p>
          </div>
        </div>
      </div>

      {/* ── BANNIÈRE NON INSCRIT ── */}
      {config && config.inscrit === 0 && alerte && (
        <div className="card tvq-banniere-warning">
          <div className="tvq-note-inner">
            <span className="tvq-note-icon warning"><Icon name="attention" size={18} /></span>
            <div style={{ flex: 1 }}>
              <strong className="warning">Escamel n'est pas encore inscrit aux fichiers de la TPS/TVQ</strong>
              <p className="muted" style={{ margin: 'var(--space-2) 0 var(--space-3)', fontSize: 13 }}>
                L'inscription est obligatoire dès que les revenus de 4 trimestres consécutifs dépassent{' '}
                <strong>{fmt$(alerte.seuil)}</strong>.
              </p>
              <div className="tvq-jauge-label">
                <span style={{ fontSize: 13 }}>
                  Revenus cumulatifs FY{anneeActuelle} :{' '}
                  <strong>{fmt$(alerte.revenus_annee_courante)}</strong> / {fmt$(alerte.seuil)}
                </span>
              </div>
              <div className="tvq-jauge-track">
                <div className="tvq-jauge-bar" style={{
                  width: `${Math.min(alerte.pct_seuil, 100)}%`,
                  background: couleurJauge(alerte.pct_seuil),
                }} />
              </div>
              <span className="muted" style={{ fontSize: 12, marginTop: 'var(--space-1)', display: 'block' }}>
                {fmtPct(alerte.pct_seuil)} du seuil atteint
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── JAUGE DE SURVEILLANCE ── */}
      {alerte && (
        <div className="card tvq-jauge-card">
          <div className="tvq-jauge-header">
            <div className="tvq-jauge-title-row">
              <Icon name="alerte" size={16} color="var(--muted)" />
              <h2 className="tvq-section-titre">Surveillance seuil {fmt$(alerte.seuil)}</h2>
            </div>
            <span className="muted" style={{ fontSize: 12 }}>4 derniers trimestres glissants</span>
          </div>
          <div className="tvq-jauge-valeurs">
            <span className="tvq-jauge-montant" style={{ color: couleurJauge(alerte.pct_seuil) }}>
              {fmt$(alerte.revenus_ytd)}
            </span>
            <span className="muted">{fmt$(alerte.seuil)}</span>
          </div>
          <div className="tvq-jauge-track">
            <div className="tvq-jauge-bar" style={{
              width: `${Math.min(alerte.pct_seuil, 100)}%`,
              background: couleurJauge(alerte.pct_seuil),
            }} />
          </div>
          <div className="tvq-jauge-footer">
            <span className="muted" style={{ fontSize: 13 }}>{fmtPct(alerte.pct_seuil)} du seuil</span>
            {alerte.pct_seuil < 75 && (
              <span className="success" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="succes" size={14} /> Sous le seuil
              </span>
            )}
            {alerte.pct_seuil >= 75 && alerte.pct_seuil < 90 && (
              <span className="warning" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="attention" size={14} /> Attention — approche du seuil
              </span>
            )}
            {alerte.pct_seuil >= 90 && (
              <span className="danger" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="alerte" size={14} /> Critique — inscription imminente
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── CONFIGURATION (collapsible) ── */}
      <div className="card tvq-config-card">
        <button className="tvq-config-toggle" onClick={() => setConfigOuvert(o => !o)}>
          <span className="tvq-config-titre">
            <Icon name="parametres" size={16} /> Configuration TPS/TVQ
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {config && (
              <span className={config.inscrit ? 'badge badge--success' : 'badge'}>
                {config.inscrit ? 'Inscrit' : 'Non inscrit'}
              </span>
            )}
            <Icon name="chevron" size={14} style={{ transform: configOuvert ? 'rotate(90deg)' : 'none', transition: 'transform var(--t-fast)' }} color="var(--muted)" />
          </div>
        </button>

        {configOuvert && config && (
          <div className="tvq-config-body">
            <div className="tvq-config-grid-2">
              <div className="field">
                <label>Numéro TPS</label>
                <input
                  value={formConfig.numero_tps || ''}
                  onChange={e => setFormConfig(f => ({ ...f, numero_tps: e.target.value }))}
                  placeholder="ex: 123456789 RT0001"
                />
              </div>
              <div className="field">
                <label>Numéro TVQ</label>
                <input
                  value={formConfig.numero_tvq || ''}
                  onChange={e => setFormConfig(f => ({ ...f, numero_tvq: e.target.value }))}
                  placeholder="ex: 1234567890 TQ0001"
                />
              </div>
              <div className="field">
                <label>Date inscription TPS</label>
                <DateInput
                  value={formConfig.date_inscription_tps || ''}
                  onChange={v => setFormConfig(f => ({ ...f, date_inscription_tps: v }))}
                />
              </div>
              <div className="field">
                <label>Date inscription TVQ</label>
                <DateInput
                  value={formConfig.date_inscription_tvq || ''}
                  onChange={v => setFormConfig(f => ({ ...f, date_inscription_tvq: v }))}
                />
              </div>
            </div>

            <div className="tvq-config-grid-3">
              <div className="field">
                <label>Taux TPS</label>
                <div className="tvq-taux-affichage">{(config.taux_tps * 100).toFixed(2)} %</div>
              </div>
              <div className="field">
                <label>Taux TVQ</label>
                <div className="tvq-taux-affichage">{(config.taux_tvq * 100).toFixed(3)} %</div>
              </div>
              <div className="field">
                <label>Fréquence de déclaration</label>
                <select
                  value={formConfig.frequence_declaration || 'trimestrielle'}
                  onChange={e => setFormConfig(f => ({ ...f, frequence_declaration: e.target.value }))}
                >
                  <option value="mensuelle">Mensuelle</option>
                  <option value="trimestrielle">Trimestrielle</option>
                  <option value="annuelle">Annuelle</option>
                </select>
              </div>
            </div>

            <label className="tvq-checkbox-label">
              <input
                type="checkbox"
                checked={!!formConfig.inscrit}
                onChange={e => setFormConfig(f => ({ ...f, inscrit: e.target.checked }))}
              />
              <span>Inscrit à la TPS/TVQ</span>
            </label>

            <div className="field" style={{ marginTop: 'var(--space-4)' }}>
              <label>Notes</label>
              <textarea
                value={formConfig.notes || ''}
                onChange={e => setFormConfig(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <button className="btn btn-primary" onClick={sauvegarderConfig} disabled={sauvegarde}>
              <Icon name="sauvegarder" size={14} />
              {sauvegarde ? 'Sauvegarde...' : 'Enregistrer'}
            </button>
          </div>
        )}
      </div>

      {/* ── SÉLECTEUR D'ANNÉE + DÉCLARATIONS ── */}
      <div className="tvq-declarations-header">
        <h2 className="tvq-section-titre">Déclarations trimestrielles</h2>
        <div className="tvq-annee-nav">
          <button className="btn btn-ghost btn-sm" onClick={() => setAnnee(a => a - 1)}>‹</button>
          <span className="tvq-annee-label">{annee}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setAnnee(a => a + 1)} disabled={annee >= anneeActuelle}>›</button>
        </div>
      </div>

      {/* ── GRILLE 4 TRIMESTRES ── */}
      <div className="tvq-trimestres-grid">
        {TRIMESTRES.map(({ num, label, periode }) => {
          const decl = getDecl(num);
          const loading = calcul[num];

          return (
            <div key={num} className="card tvq-trimestre-card rise-in">
              <div className="tvq-trimestre-header">
                <div className="tvq-trimestre-top">
                  <span className="tvq-trimestre-label">{label}</span>
                  {decl && badgeStatut(decl.statut)}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>{periode}</div>
              </div>

              {decl ? (
                <>
                  <TaxeRow label="TPS collectée"   val={decl.tps_collectee} />
                  <TaxeRow label="CTI TPS"          val={decl.tps_cti}       negatif />
                  <TaxeRow label="TPS nette"        val={decl.tps_nette}     bold />
                  <div className="tvq-separateur" />
                  <TaxeRow label="TVQ collectée"   val={decl.tvq_collectee} />
                  <TaxeRow label="RTI TVQ"          val={decl.tvq_rti}       negatif />
                  <TaxeRow label="TVQ nette"        val={decl.tvq_nette}     bold />
                  <div className="tvq-separateur" />
                  <div className="tvq-net-row">
                    <span className="tvq-net-label">NET</span>
                    <span className={decl.total_a_remettre > 0 ? 'danger' : 'success'} style={{ fontWeight: 700, fontSize: 14, fontFeatureSettings: "'tnum' 1" }}>
                      {fmt$(decl.total_a_remettre)}
                    </span>
                  </div>

                  {decl.statut === 'en_cours' && (
                    <button className="btn btn-ghost btn-sm" style={{ width: '100%', marginTop: 'var(--space-3)' }} onClick={() => changerStatut(decl.id, 'produite')}>
                      Marquer produite
                    </button>
                  )}
                  {decl.statut === 'produite' && (
                    <button className="btn btn-primary btn-sm" style={{ width: '100%', marginTop: 'var(--space-3)' }} onClick={() => changerStatut(decl.id, 'payee')}>
                      <Icon name="check" size={13} /> Marquer payée
                    </button>
                  )}
                </>
              ) : (
                <div className="muted" style={{ fontSize: 13, marginBottom: 'var(--space-3)' }}>Pas encore calculée</div>
              )}

              <button
                className="btn btn-ghost btn-sm"
                style={{ width: '100%', marginTop: decl ? 'var(--space-2)' : 0 }}
                onClick={() => calculerTrimestre(num)}
                disabled={loading}
              >
                <Icon name="recalculer" size={13} />
                {loading ? 'Calcul...' : decl ? 'Recalculer' : 'Calculer'}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── RÉSUMÉ ANNUEL ── */}
      {resume && (
        <div className="card tvq-resume-card">
          <div className="card-header" style={{ marginBottom: 'var(--space-5)' }}>
            <div className="card-header-left">
              <span className="card-header-icon"><Icon name="analytics" size={16} /></span>
              <span className="card-title">Résumé TPS/TVQ — FY{annee}</span>
            </div>
          </div>
          <div className="tvq-resume-grid">
            <div>
              <div className="tvq-resume-section-label">TPS</div>
              <TaxeRow label="TPS collectée"   val={resume.tps_collectee_total} />
              <TaxeRow label="CTI TPS"          val={resume.tps_cti_total}       negatif />
              <TaxeRow label="TPS nette"        val={resume.tps_nette_total}     bold />
            </div>
            <div>
              <div className="tvq-resume-section-label">TVQ</div>
              <TaxeRow label="TVQ collectée"   val={resume.tvq_collectee_total} />
              <TaxeRow label="RTI TVQ"          val={resume.tvq_rti_total}       negatif />
              <TaxeRow label="TVQ nette"        val={resume.tvq_nette_total}     bold />
            </div>
          </div>
          <div className="tvq-resume-total">
            <div className="tvq-resume-total-row">
              <span className="tvq-resume-total-label">TOTAL À REMETTRE</span>
              <span className={resume.total_a_remettre > 0 ? 'danger' : 'success'} style={{ fontFamily: 'Cinzel, serif', fontSize: 22, fontWeight: 600, fontFeatureSettings: "'tnum' 1, 'lnum' 1" }}>
                {fmt$(resume.total_a_remettre)}
              </span>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 'var(--space-1)' }}>
              Positif = vous payez · Négatif = vous recevez un remboursement
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── COMPOSANT LIGNE TAXE ────────────────────
function TaxeRow({ label, val, negatif, bold }) {
  return (
    <div className="tvq-taxe-row">
      <span className="muted" style={{ fontSize: 12 }}>{label}</span>
      <span style={{
        fontSize: 13,
        fontWeight: bold ? 700 : 400,
        fontFeatureSettings: "'tnum' 1",
        color: negatif && val > 0 ? 'var(--success)' : undefined,
      }}>
        {negatif && val > 0 ? '- ' : ''}{fmt$(val)}
      </span>
    </div>
  );
}
